import { useState, useCallback, useMemo } from "react";
import type { InstallmentLead } from "./useOperationsAnalytics";

// =============================================================================
// useInstallmentFilters
// =============================================================================
// PURPOSE: Click-to-filter state machine for the Installment Tracking table.
// Adapted from useTransactionFilters — same UX pattern (left-click isolate,
// right-click exclude, chip click add-back).
//
// FILTER MODEL:
// - Each column maintains a Set<string> of *included* values
// - Left-click  (isolate): set included = Set([clickedValue]), toggle off if same
// - Right-click (exclude): remove clickedValue from included set
// - Columns combine with AND; values within a column combine with OR
// =============================================================================

export type InstallmentFilterColumn = "closer" | "setter" | "program" | "status";

export type FilterMode = "isolate" | "exclude";

export interface InstallmentFilterChip {
  column: InstallmentFilterColumn;
  value: string;
}

export interface InstallmentColumnFilter {
  column: InstallmentFilterColumn;
  includedValues: Set<string>;
  allValues: Set<string>;
}

function getLeadStatus(lead: InstallmentLead): string {
  if (lead.isAbandoned) return "Abandoned";
  if (lead.isFullyPaid) return "Paid";
  if (!lead.nextPaymentDate) return "No date";
  if (lead.isOverdue || (lead.nextPaymentDate && new Date(lead.nextPaymentDate) < new Date()))
    return "Overdue";
  return "Active";
}

export function getInstallmentColumnValue(
  lead: InstallmentLead,
  column: InstallmentFilterColumn
): string {
  switch (column) {
    case "closer":
      return lead.closerName || "Unassigned";
    case "setter":
      return lead.setterName || "Unassigned";
    case "program":
      return lead.program || "—";
    case "status":
      return getLeadStatus(lead);
    default:
      return "";
  }
}

function computeAllValues(leads: InstallmentLead[], column: InstallmentFilterColumn): Set<string> {
  const values = new Set<string>();
  for (const lead of leads) {
    values.add(getInstallmentColumnValue(lead, column));
  }
  return values;
}

function isRedundantSet(includedValues: Set<string>, allValues: Set<string>): boolean {
  if (allValues.size === 0) return true;
  if (includedValues.size < allValues.size) return false;
  for (const v of allValues) {
    if (!includedValues.has(v)) return false;
  }
  return true;
}

export interface UseInstallmentFiltersResult {
  filters: Map<InstallmentFilterColumn, InstallmentColumnFilter>;
  activeFilterCount: number;
  applyFilter: (column: InstallmentFilterColumn, value: string, mode: FilterMode) => void;
  removeFilter: (column: InstallmentFilterColumn, value: string) => void;
  clearColumnFilter: (column: InstallmentFilterColumn) => void;
  clearAllFilters: () => void;
  getFilteredData: (leads: InstallmentLead[]) => InstallmentLead[];
  getChips: () => InstallmentFilterChip[];
}

export function useInstallmentFilters(leads: InstallmentLead[]): UseInstallmentFiltersResult {
  const [includedMap, setIncludedMap] = useState<Map<InstallmentFilterColumn, Set<string>>>(
    new Map()
  );

  const filters = useMemo((): Map<InstallmentFilterColumn, InstallmentColumnFilter> => {
    const result = new Map<InstallmentFilterColumn, InstallmentColumnFilter>();
    for (const [column, includedValues] of includedMap) {
      const allValues = computeAllValues(leads, column);
      if (!isRedundantSet(includedValues, allValues)) {
        result.set(column, { column, includedValues, allValues });
      }
    }
    return result;
  }, [includedMap, leads]);

  const applyFilter = useCallback(
    (column: InstallmentFilterColumn, value: string, mode: FilterMode) => {
      setIncludedMap((prev) => {
        const next = new Map(prev);

        if (mode === "isolate") {
          const existing = prev.get(column);
          if (existing && existing.size === 1 && existing.has(value)) {
            next.delete(column);
          } else {
            next.set(column, new Set([value]));
          }
        } else {
          const existing = prev.get(column);
          const allValues = computeAllValues(leads, column);
          const includedValues = existing ? new Set(existing) : new Set(allValues);
          includedValues.delete(value);

          if (includedValues.size === 0) {
            next.set(column, includedValues);
          } else if (isRedundantSet(includedValues, allValues)) {
            next.delete(column);
          } else {
            next.set(column, includedValues);
          }
        }

        return next;
      });
    },
    [leads]
  );

  const removeFilter = useCallback(
    (column: InstallmentFilterColumn, value: string) => {
      setIncludedMap((prev) => {
        const existing = prev.get(column);
        if (!existing) return prev;

        const next = new Map(prev);
        const includedValues = new Set(existing);
        includedValues.add(value);

        const allValues = computeAllValues(leads, column);
        if (isRedundantSet(includedValues, allValues)) {
          next.delete(column);
        } else {
          next.set(column, includedValues);
        }

        return next;
      });
    },
    [leads]
  );

  const clearColumnFilter = useCallback((column: InstallmentFilterColumn) => {
    setIncludedMap((prev) => {
      if (!prev.has(column)) return prev;
      const next = new Map(prev);
      next.delete(column);
      return next;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setIncludedMap(new Map());
  }, []);

  const activeFilterCount = filters.size;

  const getFilteredData = useCallback(
    (data: InstallmentLead[]): InstallmentLead[] => {
      if (filters.size === 0) return data;

      return data.filter((lead) => {
        for (const [column, filter] of filters) {
          const value = getInstallmentColumnValue(lead, column);
          if (!filter.includedValues.has(value)) return false;
        }
        return true;
      });
    },
    [filters]
  );

  const getChips = useCallback((): InstallmentFilterChip[] => {
    const chips: InstallmentFilterChip[] = [];
    for (const [column, filter] of filters) {
      for (const value of filter.allValues) {
        if (!filter.includedValues.has(value)) {
          chips.push({ column, value });
        }
      }
    }
    return chips;
  }, [filters]);

  return {
    filters,
    activeFilterCount,
    applyFilter,
    removeFilter,
    clearColumnFilter,
    clearAllFilters,
    getFilteredData,
    getChips,
  };
}
